import type { EndpointBuilder } from "@reduxjs/toolkit/dist/query/endpointDefinitions";
import type {
    ActivityCreatePayload,
    ActivityDeletePayload,
    ActivityResponse,
    ActivityResultsResponse,
    ActivityUpdatePayload,
    FastActivityResponse,
    GetStatusesResponse,
    ModuleResponse,
} from "./activityTypes";
import { BEModulesObj } from "@utils/moduleList";
import { EmissionsTotal } from "@pages/project/results/hooks/resultTypes";
import type { TagTypes } from "@app/apiSlice";
import type { ApiSliceType, PublicApiSliceType, Activity, Module } from "@typedefs/interfaces";
import { Tool } from "../auth/authTypes";

export function createActivityApi(apiSlice: ApiSliceType | PublicApiSliceType) {
    const endpoints = (builder: EndpointBuilder<any, TagTypes, string>) => ({
        getActivitiesByProject: builder.query<ActivityResponse, string>({
            query: (url) => url,
            transformResponse: (response: ActivityResponse) => {
                response.results.sort((a, b) => a.id > b.id ? -1 : 1)
                return response
            },
            providesTags: (res, error, arg) =>
                res
                    ? [
                        { type: "Activities", id: "LIST" },
                        ...res.results.map((a) => ({ type: "Activities" as const, id: a.id })),
                    ]
                    : [{ type: "Activities", id: "LIST" }],
        }),
        getFastActivitiesByProject: builder.query<FastActivityResponse, { projectId: number, page?: number, pageSize?: number, tool?: Tool, ready?: boolean }>({
            query: ({ projectId, page, pageSize, tool, ready }) => {
                const params = new URLSearchParams();
                params.append('project_id', projectId.toString());
                if (page !== undefined) params.append('page', page.toString());
                if (pageSize !== undefined) params.append('page_size', pageSize.toString());
                if(tool !== undefined) params.append('is_b_intact', tool === Tool.B_INTACT ? "true" : "false");
                if (ready !== undefined) params.append('ready', ready.toString());
                return `activities/?${params.toString()}&summary=true`;
            },
            providesTags: (_res, _error, { projectId }) => [{ type: "FastActivities", id: projectId }],
        }),
        getModulesByActivityId: builder.query<Module[], number>({
            query: (id) => `activities/${id}/modules/?page_size=100`,
            transformResponse: (response: ModuleResponse) => response.results,
            providesTags: (_res, _error, id) => [{ type: "Modules", id }]
        }),
        getActivitiesById: builder.query<Activity, number>({
            query: (id) => `activities/${id}/`,
            providesTags: (res, error, id) => [{ type: "Activities", id }],
        }),
        updateActivity: builder.mutation<Activity, ActivityUpdatePayload>({
            query: ({ id, activity }) => ({
                url: `activities/${id}/`,
                method: "PATCH",
                body: activity,
            }),
            // Optimistic update
            // async onQueryStarted({ id, activity }, { dispatch, queryFulfilled }) {
            //     const patchResult = dispatch(
            //         apiSlice.util.updateQueryData('getActivitiesById', id, (draft: Activity) => {
            //             Object.assign(draft, activity)
            //         })
            //     )
            //     try {
            //         await queryFulfilled
            //     } catch {
            //         patchResult.undo()
            //     }
            // },
            invalidatesTags: (result, _error, { id }) => [
                { type: "Activities", id },
                { type: "FastActivities", id: result?.project?.id },
                { type: "Modules", id },
                { type: "Activities", id: "LIST" },
            ],
        }),
        createActivity: builder.mutation<Activity, ActivityCreatePayload>({
            query: (activity) => ({
                url: "activities/build/",
                method: "POST",
                body: activity,
            }),
            invalidatesTags: (result, _error, arg) => [
                { type: "Activities", id: "LIST" }, 
                { type: "FastActivities", id: result?.project?.id }, 
                ...(arg.activity_id ? [{ type: "Activities" as const, id: arg.activity_id }] : [])
            ]
        }),
        createBIntactActivity: builder.mutation<Activity, ActivityCreatePayload>({
            query: (activity) => ({
                url: "activities/",
                method: "POST",
                body: {
                    ...activity,
                    is_b_intact: true,
                },
            }),
            invalidatesTags: (result, _error, arg) => [
                { type: "Activities", id: "LIST" }, 
                { type: "FastActivities", id: result?.project?.id }, 
                ...(arg.activity_id ? [{ type: "Activities" as const, id: arg.activity_id }] : [])
            ]
        }),
        deleteActivity: builder.mutation<Activity, ActivityDeletePayload>({
            query: ({ id }) => ({ url: `activities/${id}/`, method: "DELETE" }),
            invalidatesTags: (result, error, arg) => [
                { type: "Activities", id: arg.id },
                { type: "Activities", id: "LIST" },
                { type: "FastActivities", id: result?.project?.id },
            ],
        }),
        createActivityCopy: builder.mutation<Activity, number>({
            query: (id) => ({ url: `activities/${id}/copy/`, method: "POST" }),
            invalidatesTags: (result, _error, id) => [
                { type: "Activities", id },
                { type: "FastActivities", id: result?.project?.id },
            ],
        }),
        getModulesStatues: builder.query<GetStatusesResponse[], number>({
            query: (activityId) => `activities/${activityId}/modules/`,
            transformResponse: (response: ModuleResponse) => response.results?.map((mod) => {
                const feModuleId = BEModulesObj[mod.module_type.class_name];
                return { id: mod.id, status: mod.status, feModuleId }
            }),
            providesTags: (_res, _error, activityId) => [{ type: "ModulesStatuses", id: activityId }]
        }),
        getActivityResults: builder.query<EmissionsTotal, number>({
            query: (id) => `activities/${id}/results/`,
            providesTags: (_res, _error, id) => [{ type: "ActivityResults", id }],
            transformResponse: (response: ActivityResultsResponse) => {
                return response.modules.reduce((acc, curr) => {
                    if (!curr.results || 'details' in curr.results) return acc;
                    if ('total_w' in curr.results) {
                        acc.total_w += curr.results.total_w ?? 0;
                        acc.total_wo += curr.results.total_wo ?? 0;
                        acc.balance += curr.results.balance ?? 0;
                        // Sum inventory if present, otherwise it defaults to 0
                        if ('inventory' in curr.results && typeof curr.results.inventory === 'number') {
                            acc.inventory += curr.results.inventory;
                        }
                    }
                    return acc;
                }, { total_w: 0, total_wo: 0, balance: 0, inventory: 0 });
            }
        })
    });

    return apiSlice.injectEndpoints({ endpoints });
} 